// The mobile web → app hand-off.
//
// Members get links by email/push (a fixture, a challenge, a meet date); on a
// phone those pages are better in the app, and universal links only take
// people there when the app is installed AND the link was tapped outside an
// in-app browser (Gmail/Facebook/Instagram webviews don't fire them). So on a
// mobile device, member-facing pages show a fullscreen "get the app" step
// once, with "Maybe later" snoozing it for 7 days (cookie + localStorage +
// an HTTP-set cookie via POST /api/app-handoff — Safari's ITP caps
// script-written cookies at 7 days, exactly the snooze length).
//
// Never shown on invite/register/login/validate (registering on the web from
// an invite link is right), admin, embeds, live scoring, pay, or marketing
// pages (those keep their static banners). `?app=0` is the support escape
// hatch. After an invite join, the joined entity's page shows the post-join
// variant regardless of snooze (that is the moment to hand off).
import {computed, watch} from 'vue';
import {APP_STORE, IOS_APP_ID, appSchemeUrl} from '~/utils/appLinks';

export const HANDOFF_COOKIE = 'tl-app-handoff';
export const HANDOFF_VERSION = 1;
export const HANDOFF_SNOOZE_DAYS = 7;

// Pages a member is sent to by email/push, or reaches signed in.
const PROMPT_ROUTE_NAMES = new Set([
  'FixtureDetail', 'LadderChallengeDetail', 'LadderChallenges',
  'MeetOccurrenceDetail', 'MeetDetail', 'Chat', 'ConversationLegacy', 'MessagingLegacy',
  'LadderDetail', 'LeagueDetail', 'TournamentDetail', 'AmericanoDetail', 'ClubDetail',
]);
const EXCLUDED_PREFIXES = ['/admin', '/embed', '/livescoring', '/pay', '/auth', '/invite', '/login', '/register',
  '/validate', '/details-required', '/login-with-token', '/meets/offer'];

const COOKIE_OPTIONS = {maxAge: 60 * 60 * 24 * 30, sameSite: 'lax', path: '/'};

/** SSR-safe device facts — the user-agent header on the server, navigator on the client (useDeviceLocale pattern). */
export function useMobileDevice() {
  return useState('mobileDevice', () => {
    const ua = import.meta.server
      ? (useRequestHeaders(['user-agent'])['user-agent'] || '')
      : (typeof navigator !== 'undefined' ? navigator.userAgent : '');
    const ios = /iPhone|iPad|iPod/i.test(ua);
    const android = /Android/i.test(ua) && /Mobile/i.test(ua);
    const inApp = /FBAN|FBAV|Instagram|Line\/|GSA\/|Twitter|LinkedInApp|Snapchat|; wv\)/i.test(ua);
    return {isMobile: ios || android, platform: ios ? 'ios' : (android ? 'android' : null), inAppBrowser: inApp};
  });
}

export function useMobileApp() {
  const device = useMobileDevice();
  const route = useRoute();
  const cookie = useCookie(HANDOFF_COOKIE, COOKIE_OPTIONS);
  const requestUrl = useRequestURL();
  const {isAuthenticated} = useAuthService();

  // Once per SSR session, whatever the cookie says — and the post-join hand-off.
  const shownThisSession = useState('appHandoffShown', () => false);
  const open = useState('appHandoffOpen', () => false);
  const postJoin = useState('appHandoffPostJoin', () => null); // {path, name}

  const snoozedUntil = computed(() => {
    const v = cookie.value;
    if (!v || typeof v !== 'object' || v.v !== HANDOFF_VERSION) return null;
    const t = Date.parse(v.until);
    return Number.isNaN(t) ? null : t;
  });
  const snoozed = computed(() => snoozedUntil.value != null && snoozedUntil.value > Date.now());

  const routeEligible = computed(() => {
    const path = route.path || '/';
    if (EXCLUDED_PREFIXES.some((p) => path === p || path.startsWith(p + '/') || path.startsWith(p + '?'))) return false;
    if (route.query.app === '0') return false;
    if (route.name === 'Home') return isAuthenticated.value === true;
    return PROMPT_ROUTE_NAMES.has(route.name);
  });

  // Eligible = the page + device qualify; `open` is the interstitial actually
  // being up. Opening marks the session so navigating on does not re-open it,
  // and a post-join hand-off opens regardless of snooze.
  const eligible = computed(() => {
    if (!device.value.isMobile) return false;
    if (postJoin.value) return true;
    if (shownThisSession.value || snoozed.value) return false;
    return routeEligible.value;
  });
  const shouldPrompt = computed(() => open.value);

  // ── persistence: cookie + localStorage + HTTP-set cookie ──
  const persistServerSide = (value) => {
    $fetch('/api/app-handoff', {method: 'POST', body: value}).catch(() => {});
  };
  const restoreFromLocalStorage = () => {
    if (import.meta.server || cookie.value) return;
    try {
      const raw = window.localStorage.getItem(HANDOFF_COOKIE);
      if (!raw) return;
      const stored = JSON.parse(raw);
      if (stored?.v === HANDOFF_VERSION) {
        cookie.value = stored;
        persistServerSide(stored);
      }
    } catch { /* corrupt copy — it will just ask again */ }
  };
  const snooze = () => {
    const value = {v: HANDOFF_VERSION, until: new Date(Date.now() + HANDOFF_SNOOZE_DAYS * 864e5).toISOString()};
    cookie.value = value;
    try { window.localStorage.setItem(HANDOFF_COOKIE, JSON.stringify(value)); } catch { /* private mode */ }
    persistServerSide(value);
    shownThisSession.value = true;
    close();
    track('later');
  };

  // ── opening the app ──
  const targetPath = computed(() => postJoin.value?.path || route.fullPath || '/');
  const schemeUrl = computed(() => appSchemeUrl(requestUrl.host, targetPath.value));
  const storeUrl = computed(() => (device.value.platform === 'android' ? APP_STORE.android : APP_STORE.ios));

  /**
   * Try the app, then the store. The custom scheme opens the app when installed
   * (works inside in-app browsers too); if this page is still visible ~1.5s
   * later the app is not there, so go to the store.
   */
  const openInApp = () => {
    track('open_app');
    shownThisSession.value = true;
    open.value = false;
    const started = Date.now();
    const timer = setTimeout(() => {
      if (document.visibilityState === 'visible' && Date.now() - started < 4000) {
        track('store_fallback');
        window.location.href = storeUrl.value;
      }
    }, 1500);
    const cancel = () => { if (document.visibilityState !== 'visible') clearTimeout(timer); };
    document.addEventListener('visibilitychange', cancel, {once: true});
    window.location.href = schemeUrl.value;
  };
  const openStore = () => {
    track('store');
    shownThisSession.value = true;
    open.value = false;
    window.location.href = storeUrl.value;
  };
  const close = () => { open.value = false; postJoin.value = null; };
  const clearPostJoin = () => { postJoin.value = null; };

  /** Client boot + every route change: open once when the page qualifies. */
  const evaluate = () => {
    if (import.meta.server || open.value) return;
    if (eligible.value) {
      open.value = true;
      shownThisSession.value = true;
      track('shown');
    }
  };

  // Best-effort analytics — only when the user allowed analytics (gtag present).
  const track = (action) => {
    try {
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'app_handoff', {action, platform: device.value.platform, in_app_browser: device.value.inAppBrowser, path: route.path});
      }
    } catch { /* never let analytics break the flow */ }
  };

  // iOS Smart App Banner (Safari's own, deep-linked to this page) on the same pages.
  const smartBannerContent = computed(() =>
    device.value.platform === 'ios' && routeEligible.value
      ? `app-id=${IOS_APP_ID}, app-argument=${requestUrl.origin}${route.fullPath}`
      : null);
  useHead({
    meta: computed(() => (smartBannerContent.value ? [{name: 'apple-itunes-app', content: smartBannerContent.value}] : [])),
  });

  const applyOnLoad = () => {
    restoreFromLocalStorage();
    evaluate();
    watch(() => [route.fullPath, postJoin.value, isAuthenticated.value], () => evaluate());
  };

  return {
    device, shouldPrompt, postJoin, snoozed, routeEligible, eligible,
    schemeUrl, storeUrl, openInApp, openStore, snooze, clearPostJoin, applyOnLoad,
    /** Call after an invite join, before navigating to the entity: the entity page then hands off. */
    setPostJoin: (path, name) => { postJoin.value = {path, name}; },
  };
}
